import { createSmartrrModal } from "@vendor-app/utils/createModalStore";
import { HomeTableAccess } from "../../libs/store";
import { ConfirmationWindow } from "@vendor-app/app/_sharedComponents/ConfirmationWindow";
import { generateQueryFromStore } from "../../libs/store/query";
import { serializePaginatedQueryValues } from "@smartrr/shared/utils/paginatedQuery";
import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";
import { IFailedSubscriptionWithReason } from "@vendor-app/app/_sharedComponents/SubscriptionErrorModal";
import { IPurchaseStateWithCustomerRelationshipPaginatedResponse } from "@smartrr/shared/entities/PurchaseState";
import { useBulkActionErrorModal } from "@vendor-app/app/AdminRoute/AdminSubscriptionTableRoute/components/Modals/Error";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";
import { useCallback, useContext } from "react";
import { BulkActionCompletedContext } from "../../libs/context/bulkActionCompletedContext";

export const useActivateConfirmationModal = createSmartrrModal<{}>();

export const ActivateConfirmationModal = () => {
  const selectedResources = HomeTableAccess.useSelectedData();
  const tableActions = HomeTableAccess.useActions();
  const { isModalOpen, closeModal } = useActivateConfirmationModal();
  const openErrorModal = useBulkActionErrorModal(state => state.openModal);
  const { addToast } = useToast();
  const bulkActionCompleted = useContext(BulkActionCompletedContext);

  const handleActivationConfirmation = useCallback(() => {
    tableActions.data.update(async (currentData, selectedFilters, selectedSort, pagination) => {
      closeModal();
      const query = generateQueryFromStore(selectedFilters, selectedSort, pagination);

      const response = await typedFrontendVendorApi.putReq("/customer/purchase-state/bulk-unpause", {
        query: serializePaginatedQueryValues(query),
        reqBody: {
          customerPurchaseStateIds: selectedResources.map(cps => cps.id),
        },
      });

      bulkActionCompleted.onCompleted();

      if (response.type === "success") {
        const failedSubscriptions: IFailedSubscriptionWithReason[] = response.body.failedSubscriptions;
        const updatedSubscriptions: IPurchaseStateWithCustomerRelationshipPaginatedResponse =
          response.body.newCustomerPurchaseState;

        if (failedSubscriptions.length) {
          openErrorModal({
            actionType: "unpause",
            failedSubscriptions,
          });
        }

        addToast("Subscription(s) activated");

        return {
          data: updatedSubscriptions.data,
          pagination: {
            maxPages: updatedSubscriptions.totalPages,
            pageNumber: updatedSubscriptions.pageNumber + 1,
          },
        };
      }

      addToast("Error activating subscription(s). Try to reload the page.");

      return {
        data: [],
        pagination: {
          maxPages: 1,
          pageNumber: 1,
        },
      };
    });
  }, [selectedResources, bulkActionCompleted]);

  if (!isModalOpen || selectedResources.length === 0) {
    return null;
  }

  return (
    <ConfirmationWindow
      title={`Are you sure you want to activate ${selectedResources.length} subscription(s)?`}
      onReject={closeModal}
      onConfirm={handleActivationConfirmation}
      confirmationText="Yes"
      rejectionText="No"
      open={isModalOpen}
    >
      It might take a few minutes to process this request.
    </ConfirmationWindow>
  );
};
